import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'localization';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { getContextViewsByContext } from '../../../api/contextApi';
import type { ContextViewRecord } from '../../../api/contextApi';
import { DEFAULT_VIEW_FORM_STATE, getDefaultViewConfig } from '../ContextViewEditor/ContextViewEditorOrganism';
import type { ContextViewFormState, ViewTypeValue } from '../ContextViewEditor/ContextViewEditorOrganism';
import ContextViewEditorOverlayOrganism from '../ContextViewEditorOverlay/ContextViewEditorOverlayOrganism';
import ContextViewCardMolecule from '../../molecules/ContextViewCard/ContextViewCardMolecule';
import './ContextViewsTabOrganism.scss';

// ============================================================================
// Constants
// ============================================================================

const ALL_SURFACES: ViewTypeValue[] = ['atlas', 'treemap', 'kanban', 'table', 'timeline'];

const VIEW_TYPE_ICONS: Record<string, string> = {
  atlas: 'pi pi-compass',
  treemap: 'pi pi-th-large',
  kanban: 'pi pi-server',
  table: 'pi pi-table',
  timeline: 'pi pi-calendar',
};

// ============================================================================
// Types
// ============================================================================

interface ViewEditorState {
  mode: 'new' | 'edit';
  viewSystemName?: string;
  seedForm?: ContextViewFormState;
}

export interface ContextViewsTabOrganismProps {
  contextSystemName: string;
  contextDisplayName: string;
  readOnly?: boolean;
  onCountChange?: (count: number) => void;
  onAnalyticsEvent?: (event: string, meta: Record<string, unknown>) => void;
  onNavigateToLibrary?: () => void;
}

// ============================================================================
// Component
// ============================================================================

const ContextViewsTabOrganism: React.FC<ContextViewsTabOrganismProps> = ({
  contextSystemName,
  contextDisplayName,
  readOnly,
  onCountChange,
  onAnalyticsEvent,
  onNavigateToLibrary,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();

  const [views, setViews] = useState<ContextViewRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [viewEditorState, setViewEditorState] = useState<ViewEditorState | null>(null);

  // Stable callback refs — avoids including rapidly-changing parent functions in effect deps
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;

  const onAnalyticsEventRef = useRef(onAnalyticsEvent);
  onAnalyticsEventRef.current = onAnalyticsEvent;

  // Read the API client through a ref so the fetch effect does not depend on the
  // clients object identity (it is re-memoized whenever the PWA's onResponse/onError
  // observers change, which would otherwise cause refetch loops). Re-running is driven
  // instead by the `hasSchemaClient` boolean below — false→true when auth init lands.
  const apiClientsRef = useRef(apiClients);
  apiClientsRef.current = apiClients;
  const hasSchemaClient = !!apiClients?.schemaClient;

  // Fired once after the first successful load (mount-level tab-opened event)
  const hasFiredTabOpenedRef = useRef(false);

  useEffect(() => {
    const schemaClient = apiClientsRef.current?.schemaClient;
    if (!schemaClient) return;

    let cancelled = false;
    setLoading(true);
    setError(false);

    getContextViewsByContext(schemaClient, contextSystemName)
      .then((fetched) => {
        if (cancelled) return;
        const sorted = [...fetched].sort((a, b) => a.system_name.localeCompare(b.system_name));
        setViews(sorted);
        onCountChangeRef.current?.(sorted.length);
        if (!hasFiredTabOpenedRef.current) {
          hasFiredTabOpenedRef.current = true;
          onAnalyticsEventRef.current?.('context_views_tab_opened', {
            contextSystemName,
            viewCount: sorted.length,
          });
        }
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });

    return () => { cancelled = true; };
  // hasSchemaClient retriggers the fetch when auth init populates the clients (false→true)
  // without coupling to the clients object identity; apiClients is read via ref above.
  }, [contextSystemName, refreshKey, hasSchemaClient]);

  // Other surfaces = standard types not represented in the fetched views
  const presentViewTypes = new Set(views.map((v) => v.view_type));
  const otherSurfaces = ALL_SURFACES.filter((surface) => !presentViewTypes.has(surface));

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleNewView = useCallback(() => {
    onAnalyticsEventRef.current?.('context_view_create_opened', {
      contextSystemName,
      seededViewType: null,
    });
    setViewEditorState({ mode: 'new' });
  }, [contextSystemName]);

  const handleOpenCard = useCallback((view: ContextViewRecord) => {
    onAnalyticsEventRef.current?.('context_view_edit_opened', {
      contextSystemName,
      viewSystemName: view.system_name,
      viewType: view.view_type,
    });
    setViewEditorState({ mode: 'edit', viewSystemName: view.system_name });
  }, [contextSystemName]);

  const handleOpenSurface = useCallback((viewType: ViewTypeValue) => {
    onAnalyticsEventRef.current?.('context_view_create_opened', {
      contextSystemName,
      seededViewType: viewType,
    });
    const seedForm: ContextViewFormState = {
      ...DEFAULT_VIEW_FORM_STATE,
      contextRef: contextSystemName,
      viewType,
      // viewConfig must match viewType — DEFAULT_VIEW_FORM_STATE is atlas-shaped,
      // so rebuild it for the chosen surface (otherwise the editor renders the
      // wrong type-specific body and saves a mismatched discriminated union).
      viewConfig: getDefaultViewConfig(viewType),
    };
    setViewEditorState({ mode: 'new', seedForm });
  }, [contextSystemName]);

  const handleSaved = useCallback((_savedSn: string) => {
    setViewEditorState(null);
    setRefreshKey((k) => k + 1);
  }, []);

  const handleDeleted = useCallback((_deletedSn: string) => {
    setViewEditorState(null);
    setRefreshKey((k) => k + 1);
  }, []);

  // ── Loading / Error states ──────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="context-views-tab-organism context-views-tab-organism--loading">
        <i className="pi pi-spin pi-spinner" aria-label={t('common.loading')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="context-views-tab-organism context-views-tab-organism--error">
        <span>{t('context.editor.views.loadError')}</span>
        <button
          className="context-views-tab-organism__retry-btn"
          type="button"
          onClick={() => setRefreshKey((k) => k + 1)}
        >
          {t('context.editor.views.retry')}
        </button>
      </div>
    );
  }

  // ── Main render ─────────────────────────────────────────────────────────────

  return (
    <div className="context-views-tab-organism">

      {/* Views section */}
      <div className="context-views-tab-organism__section">
        <div className="context-views-tab-organism__section-header">
          <div className="context-views-tab-organism__title-group">
            <span className="context-views-tab-organism__section-title">
              {t('context.editor.views.sectionTitle')}
            </span>
            <span className="context-views-tab-organism__section-subtitle">
              {t('context.editor.views.sectionSubtitle')}
            </span>
          </div>
          {!readOnly && (
            <button
              className="context-views-tab-organism__new-view-btn"
              type="button"
              onClick={handleNewView}
            >
              <i className="pi pi-plus" aria-hidden="true" />
              {t('context.editor.views.newView')}
            </button>
          )}
        </div>

        {views.length === 0 ? (
          <div className="context-views-tab-organism__empty">
            <span className="context-views-tab-organism__empty-title">
              {t('context.editor.views.emptyTitle')}
            </span>
            <span className="context-views-tab-organism__empty-body">
              {t('context.editor.views.emptyBody')}
            </span>
          </div>
        ) : (
          <div className="context-views-tab-organism__grid">
            {views.map((view) => (
              <ContextViewCardMolecule
                key={view.system_name}
                view={view}
                onOpen={handleOpenCard}
                disabled={readOnly}
              />
            ))}
          </div>
        )}
      </div>

      {/* Other surfaces section */}
      {otherSurfaces.length > 0 && (
        <div className="context-views-tab-organism__section">
          <div className="context-views-tab-organism__title-group">
            <span className="context-views-tab-organism__section-title">
              {t('context.editor.views.otherSurfacesTitle')}
            </span>
            <span className="context-views-tab-organism__section-subtitle">
              {t('context.editor.views.otherSurfacesSubtitle')}
            </span>
          </div>
          <div className="context-views-tab-organism__surfaces">
            {otherSurfaces.map((viewType) => (
              <button
                key={viewType}
                className={`context-views-tab-organism__surface-tile context-views-tab-organism__surface-tile--${viewType}${readOnly ? ' context-views-tab-organism__surface-tile--disabled' : ''}`}
                type="button"
                disabled={readOnly}
                onClick={() => !readOnly && handleOpenSurface(viewType)}
              >
                <span className="context-views-tab-organism__surface-icon">
                  <i className={VIEW_TYPE_ICONS[viewType]} aria-hidden="true" />
                </span>
                <span className="context-views-tab-organism__surface-info">
                  <span className="context-views-tab-organism__surface-name">
                    {t(`context.viewEditor.viewTypes.${viewType}`)}
                  </span>
                  <span className="context-views-tab-organism__surface-hint">
                    {t('context.editor.views.rendersWithDefaults')}
                  </span>
                </span>
                <span className="context-views-tab-organism__surface-action">
                  {t('context.editor.views.customize')}
                  <i className="pi pi-arrow-right" aria-hidden="true" />
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* View editor overlay — absolutely positioned within this organism's bounds */}
      {viewEditorState && (
        <ContextViewEditorOverlayOrganism
          mode={viewEditorState.mode}
          contextSystemName={contextSystemName}
          contextDisplayName={contextDisplayName}
          viewSystemName={viewEditorState.viewSystemName}
          seedForm={viewEditorState.seedForm}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
          onClose={() => setViewEditorState(null)}
          onNavigateToLibrary={onNavigateToLibrary}
          onAnalyticsEvent={onAnalyticsEvent}
        />
      )}
    </div>
  );
};

export default ContextViewsTabOrganism;
